import { useEffect, useState } from "react";
import { api } from "../api";
import type { Scope, ScopeOptions } from "../types";

let cached: Promise<ScopeOptions> | null = null;
export function options(): Promise<ScopeOptions> {
  cached ??= api.scopeOptions().catch((e) => { cached = null; throw e; });
  return cached;
}

export const sorted = (o: object | undefined) => (o ? Object.keys(o).sort() : []);

/**
 * Product family → product group 1 → product group 2 → parts, from the live part master
 * (Part.ProductFamily, ProductGroup1, ProductGroup2, Name). Empty levels mean "all".
 */
export default function ScopePicker({ value, onChange }: { value: Scope; onChange: (s: Scope) => void }) {
  const [opts, setOpts] = useState<ScopeOptions | null>(null);
  const [error, setError] = useState("");
  const [skuText, setSkuText] = useState(value.skus.join(", "));
  const group2 = value.group2 ?? "";

  useEffect(() => { options().then(setOpts).catch((e) => setError(e.message)); }, []);

  const tree = opts?.hierarchy ?? {};
  const families = sorted(tree);
  const groups1 = value.family ? sorted(tree[value.family]) : [];
  const groups2 = value.family && value.group1 ? sorted(tree[value.family]?.[value.group1]) : [];

  const skuPool = Object.entries(tree)
    .filter(([f]) => !value.family || f === value.family)
    .flatMap(([, g1s]) => Object.entries(g1s)
      .filter(([g1]) => !value.group1 || g1 === value.group1)
      .flatMap(([, g2s]) => Object.entries(g2s)
        .filter(([g2]) => !group2 || g2 === group2)
        .flatMap(([, skus]) => skus)));

  const setSkus = (text: string) => {
    setSkuText(text);
    onChange({ ...value, group2, skus: text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean) });
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="grid-3">
        <label className="field">
          <span>Product family</span>
          <select className="input" value={value.family}
                  onChange={(e) => onChange({ ...value, family: e.target.value, group1: "", group2: "" })}>
            <option value="">All families</option>
            {families.map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Product group 1</span>
          <select className="input" value={value.group1} disabled={!value.family}
                  onChange={(e) => onChange({ ...value, group1: e.target.value, group2: "" })}>
            <option value="">{value.family ? "All groups" : "Pick a family first"}</option>
            {groups1.map((g) => <option key={g}>{g}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Product group 2</span>
          <select className="input" value={group2} disabled={!value.group1}
                  onChange={(e) => onChange({ ...value, group2: e.target.value })}>
            <option value="">{value.group1 ? "All groups" : "Pick group 1 first"}</option>
            {groups2.map((g) => <option key={g}>{g}</option>)}
          </select>
        </label>
      </div>
      <label className="field">
        <span>Specific parts (optional)</span>
        <input className="input" list="gentik-skus" value={skuText} placeholder="Part names, comma separated"
               onChange={(e) => setSkus(e.target.value)} />
        <datalist id="gentik-skus">
          {skuPool.slice(0, 500).map((s) => <option key={s} value={s} label={opts?.descriptions?.[s] || undefined} />)}
        </datalist>
      </label>
      <div className="small muted">
        {error ? <span className="error-text">Couldn’t load the product hierarchy: {error}</span>
          : opts ? `${skuPool.length} part${skuPool.length === 1 ? "" : "s"} match. The agent can't write outside this scope.`
          : "Loading product hierarchy…"}
      </div>
    </div>
  );
}
